import { Sparkles, Zap, TrendingUp, Users, FileSearch, BarChart3 } from 'lucide-react';
import { Card } from '@/components/ui/card';

export const AISection = () => {
  const aiFeatures = [
    {
      icon: '✨',
      title: 'Smart Onboarding',
      description: 'AI auto-assigns checklists, predicts delays, and reminds HRs of pending actions before they become bottlenecks.',
      color: 'from-blue-400/20 to-blue-600/10',
    },
    {
      icon: '📊',
      title: 'AI Performance Insights',
      description: 'Generates objective, data-backed appraisals and tracks growth trends across teams.',
      color: 'from-purple-400/20 to-purple-600/10',
    },
    {
      icon: '🎯',
      title: 'Hiring Intelligence',
      description: 'Reads resumes, compares job fit, and gives recruiters ready-to-use candidate summaries with skill gaps and salary benchmarks.',
      color: 'from-teal-400/20 to-teal-600/10',
    },
    {
      icon: '💬',
      title: 'Ask AI for Analytics',
      description: 'Just type a question like "Show me top performers this quarter" — and get instant visual dashboards, no report setup needed.',
      color: 'from-indigo-400/20 to-indigo-600/10',
    },
    {
      icon: '📄',
      title: 'Document AI',
      description: 'Auto-summarizes long policies or PDFs into short notes and even lets you search within document content.',
      color: 'from-cyan-400/20 to-cyan-600/10',
    },
  ];

  return (
    <section className="relative py-8 sm:py-10 md:py-12 bg-gradient-to-b from-[hsl(250,50%,8%)] via-[hsl(250,50%,10%)] to-[hsl(250,50%,8%)] overflow-hidden">
      {/* Animated background grid */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(100,200,255,0.1)_1px,transparent_1px),linear-gradient(rgba(100,200,255,0.1)_1px,transparent_1px)] bg-[size:50px_50px]"></div>
      </div>

      {/* Animated background elements with GPU optimization */}
      <div className="absolute top-20 right-10 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl animate-float will-change-transform"></div>
      <div className="absolute bottom-20 left-10 w-80 h-80 bg-purple-500/10 rounded-full blur-3xl animate-float will-change-transform" style={{animationDelay: '2s'}}></div>

      <div className="relative max-w-[745px] w-full mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-8 sm:mb-10 animate-fade-in">
          <div className="inline-flex items-center space-x-2 px-3 py-1.5 rounded-full bg-blue-500/10 border border-blue-500/20 mb-4">
            <Sparkles className="w-3 h-3 text-blue-400" />
            <span className="text-xs font-medium text-blue-400">AI-Powered Platform</span>
          </div>
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-3 sm:mb-4 text-white">
            hirerkey<sup className="text-base sm:text-lg">™</sup> has <span className="emphasize-light text-white">AI at every step</span>
          </h2>
          <p className="text-sm sm:text-base text-gray-300 leading-relaxed">
            It's not just an add-on; <span className="font-semibold text-white">AI is built into the foundation</span> of hirerkey<sup>™</sup>. 
            Every click, every workflow, every insight is powered by intelligence that makes work easier.
          </p>
        </div>

        {/* AI Features - Single Column */}
        <div className="space-y-4 sm:space-y-5 animate-fade-in">
          {aiFeatures.map((feature, index) => (
            <Card
              key={index}
              className={`relative p-3 sm:p-4 mb-3 sm:mb-4 bg-gradient-to-br ${feature.color} backdrop-blur-sm border border-white/10 hover:border-blue-400/30 transition-all group animate-fade-in`}
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className="flex items-start space-x-2.5 sm:space-x-3">
                <div className="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-white/10 flex items-center justify-center text-base sm:text-xl group-hover:scale-110 transition-transform">
                  {feature.icon}
                </div>
                <div className="flex-1">
                  <h3 className="text-sm sm:text-base font-bold mb-1 text-white">{feature.title}</h3>
                  <p className="text-xs sm:text-sm text-gray-300 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              </div>
            </Card>
          ))}
        </div>

        {/* AI Visualization Image - Below Features */}
        <div className="relative pt-6 sm:pt-8 animate-fade-in">
            <div className="relative rounded-xl overflow-hidden shadow-2xl group max-w-2xl mx-auto">
              {/* Glow effect */}
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/20 via-purple-500/20 to-teal-500/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              
              {/* Main image */}
              <img
                src="https://customer-assets.emergentagent.com/job_hirerprofile/artifacts/w3r7wdeu_Ai_at_core.png"
                alt="AI Technology Interface"
                className="w-full h-auto rounded-xl transform group-hover:scale-105 transition-transform duration-500"
              />
              
              {/* Overlay gradient */}
              <div className="absolute inset-0 bg-gradient-to-t from-[hsl(250,50%,8%)]/60 via-transparent to-transparent"></div>

              {/* Floating badge */}
              <div className="absolute bottom-4 left-4 right-4">
                <div className="bg-white/10 backdrop-blur-md rounded-lg p-3 border border-white/20">
                  <div className="flex items-center space-x-2">
                    <div className="w-8 h-8 rounded-lg bg-[#00d499]/20 flex items-center justify-center">
                      <Zap className="w-4 h-4 text-[#00d499]" />
                    </div>
                    <div>
                      <p className="text-white font-bold text-xs">AI-Powered Intelligence</p>
                      <p className="text-gray-300 text-[10px]">Built into every workflow</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Decorative elements with GPU optimization */}
            <div className="absolute -top-3 -right-3 w-20 h-20 bg-blue-500/20 rounded-full blur-2xl will-change-transform"></div>
            <div className="absolute -bottom-3 -left-3 w-24 h-24 bg-purple-500/20 rounded-full blur-2xl will-change-transform"></div>
          </div>

        {/* Bottom highlight */}
        <div className="mt-8 sm:mt-10 text-center animate-fade-in">
          <div className="inline-block p-3 sm:p-4 rounded-lg bg-gradient-to-r from-blue-500/10 via-purple-500/10 to-teal-500/10 border border-white/10">
            <p className="text-gray-300 text-xs max-w-2xl">
              <span className="font-semibold text-white">Real AI, Real Results:</span> Our AI doesn't just suggest—it acts, learns, and adapts to your workplace patterns, saving hours of manual work every day.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AISection;
